import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, AxiosProvider, MantineOverrideProvider, Theme, ThemeProvider } from '@uag/react-core';
import { t } from 'i18next';
import { ReactNode, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';

import { apiClient } from 'api/custom-client';
import { configurationIdentityServer } from 'app/configurationIdentityServer';
import { AppInsightsContextProvider } from 'utils/ApplicationInsightsContextProvider';

import { router } from './AppRoutes';
import { isLoginEnabled } from './authMode';
import { CatalogDataProvider } from './catalog/CatalogDataProvider';
import { installClientSideHomeLink } from './clientSideHomeLink';
import './App.css';
import '../i18n';
import { getCustomThemeOverrides } from './getCustomThemeOverrides';
import { persistQueryCacheOnUnload, restorePersistedQueryCache } from './queryPersistence';

// import { DefaultErrorBoundary } from 'shared';
// import { applicationInsightsReactPlugin } from 'utils/applicationInsights';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false, refetchOnMount: false }
    }
});
// See queryPersistence.ts: restores whatever was cached before the last full page load (e.g. the
// DOKA header logo navigating back to "/"), then keeps saving the cache for the next one.
restorePersistedQueryCache(queryClient);
persistQueryCacheOnUnload(queryClient);

// Skip both AuthProvider AND AppInsightsContextProvider whenever login is disabled (see
// authMode.ts - by default that's the static catalog export, meant to run with zero backend and
// zero internet access for offline demos/USB-stick deployments; the SPA would otherwise get stuck
// on an "invalid redirect_uri" error the moment it isn't served from the one origin/port the app
// registration allows). AppInsightsContextProvider only reads the OIDC user for telemetry
// (useOidcContext) and would itself throw without an AuthProvider ancestor.
const MaybeAuthProvider = ({ children }: { children: ReactNode }) =>
    isLoginEnabled ? <AuthProvider configuration={configurationIdentityServer}>{children}</AuthProvider> : <>{children}</>;

const MaybeAppInsightsProvider = ({ children }: { children: ReactNode }) =>
    isLoginEnabled ? <AppInsightsContextProvider>{children}</AppInsightsContextProvider> : <>{children}</>;

export const App = () => {
    const customThemeOverrides = getCustomThemeOverrides();
    useEffect(() => {
        document.title = t('pageTitle');
    });
    // The DOKA logo in the portal header goes to "/" - through the router, not as a page load that
    // would throw the loaded catalog away (see clientSideHomeLink.ts).
    useEffect(() => installClientSideHomeLink(router), []);

    return (
        <ThemeProvider theme={Theme.Doka}>
            <MantineOverrideProvider customThemeOverrides={customThemeOverrides}>
                <MaybeAuthProvider>
                    <AxiosProvider axiosClient={apiClient}>
                        <QueryClientProvider client={queryClient}>
                            <CatalogDataProvider>
                                <MaybeAppInsightsProvider>
                                    <Notifications />
                                    <RouterProvider router={router} />
                                </MaybeAppInsightsProvider>
                            </CatalogDataProvider>
                        </QueryClientProvider>
                    </AxiosProvider>
                </MaybeAuthProvider>
            </MantineOverrideProvider>
        </ThemeProvider>
    );
};
